import { useState, useEffect, useCallback } from 'react';
import { AppWindow as AppWindowIcon, Moon, Sun, XCircle } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useDesktop } from './DesktopContext';
import { APP_REGISTRY } from './appRegistry';
import { useTheme } from '@/hooks/useTheme';

/**
 * System-wide launcher / switcher, opened with Cmd+K (or Ctrl+K).
 * Launch any app, jump to an open window, or run desktop actions.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const { windows, openWindow, focusWindow, closeWindow } = useDesktop();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const run = useCallback((action: () => void) => {
    action();
    setOpen(false);
  }, []);

  const isDark = theme === 'dark';

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Command Palette" description="Launch apps and run desktop actions">
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {windows.length > 0 && (
          <>
            <CommandGroup heading="Open Windows">
              {windows.map((win) => {
                const meta = APP_REGISTRY.find((a) => a.id === win.appId);
                const Icon = meta?.icon ?? AppWindowIcon;
                return (
                  <CommandItem
                    key={win.id}
                    value={`window ${win.title} ${win.id}`}
                    onSelect={() => run(() => focusWindow(win.id))}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{win.title}</span>
                    {win.isMinimized && <CommandShortcut>minimized</CommandShortcut>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Apps">
          {APP_REGISTRY.map((app) => {
            const Icon = app.icon;
            return (
              <CommandItem
                key={app.id}
                value={`open ${app.label} ${app.title}`}
                onSelect={() => run(() => openWindow(app.id, app.title))}
              >
                <Icon className="w-4 h-4" />
                <span>{app.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem
            value="toggle theme dark light mode"
            onSelect={() => run(() => setTheme(isDark ? 'light' : 'dark'))}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span>Switch to {isDark ? 'light' : 'dark'} mode</span>
          </CommandItem>
          {windows.length > 0 && (
            <CommandItem
              value="close all windows"
              onSelect={() => run(() => windows.forEach((w) => closeWindow(w.id)))}
            >
              <XCircle className="w-4 h-4" />
              <span>Close all windows</span>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
